/**
 * Video Reels Section Script
 * Handles the Swiper carousel of reels and the custom Quickview modal with video on the left.
 */

class VideoReelsManager {
  constructor(sectionContainer) {
    this.container = sectionContainer;
    this.sectionId = this.container.getAttribute('data-section-id');

    // Quickview Modal Elements (unique inside section container)
    this.modal = this.container.querySelector('.video-quickview');
    if (this.modal) {
      this.backdrop = this.modal.querySelector('.video-quickview__backdrop');
      this.closeBtn = this.modal.querySelector('.video-quickview__close');
      this.videoPlayer = this.modal.querySelector('.video-quickview__player');
      this.loadingEl = this.modal.querySelector('.video-quickview__product-loading');
      this.detailsEl = this.modal.querySelector('.video-quickview__product-details');
    }

    this.product = null;
    this.selectedOptions = {};
    this.currentVariant = null;
    this.quantity = 1;

    this.init();
  }

  init() {
    this.initSwiper();
    this.setupQuickviewListeners();
  }

  initSwiper() {
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.initSwiper(), 50);
      return;
    }

    const swiperEl = this.container.querySelector('.video-reels__swiper');
    const prevEl = this.container.querySelector('.video-reels__arrow--prev');
    const nextEl = this.container.querySelector('.video-reels__arrow--next');
    const paginationEl = this.container.querySelector('.video-reels__dots');

    if (!swiperEl) return;

    // Retrieve column and spacing properties
    const desktopCols = parseInt(this.container.getAttribute('data-cols-desktop')) || 4;
    const mobileCols = parseFloat(this.container.getAttribute('data-cols-mobile')) || 1.5;
    const desktopSpace = parseInt(this.container.getAttribute('data-space-desktop'));
    const spaceBetweenDesktop = isNaN(desktopSpace) ? 24 : desktopSpace;
    const mobileSpace = parseInt(this.container.getAttribute('data-space-mobile'));
    const spaceBetweenMobile = isNaN(mobileSpace) ? 16 : mobileSpace;

    new Swiper(swiperEl, {
      slidesPerView: mobileCols,
      spaceBetween: spaceBetweenMobile,
      loop: true,
      grabCursor: true,
      navigation: {
        nextEl: nextEl,
        prevEl: prevEl,
      },
      pagination: paginationEl ? {
        el: paginationEl,
        clickable: true,
        bulletClass: 'video-reels__dot',
        bulletActiveClass: 'is-active',
        renderBullet: function (index, className) {
          return '<button type="button" class="' + className + '" aria-label="Go to slide ' + (index + 1) + '"></button>';
        }
      } : false,
      breakpoints: {
        768: {
          slidesPerView: Math.min(desktopCols, 2.5),
          spaceBetween: spaceBetweenDesktop,
        },
        992: {
          slidesPerView: desktopCols,
          spaceBetween: spaceBetweenDesktop,
        }
      }
    });
  }

  setupQuickviewListeners() {
    if (!this.modal) return;

    // Close button click
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeModal());
    if (this.backdrop) this.backdrop.addEventListener('click', () => this.closeModal());

    // Intercept clicks on floating product pills
    this.container.addEventListener('click', (e) => {
      const pill = e.target.closest('.video-reels__pill');
      if (pill) {
        e.preventDefault();
        e.stopPropagation();

        const handle = pill.getAttribute('data-product-handle');
        const videoUrl = pill.getAttribute('data-video-url');

        if (handle) {
          this.openModal(handle, videoUrl || '');
        }
      }
    });

    // Escape key listener when modal is open
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) {
        this.closeModal();
      }
    });
  }

  openModal(handle, videoUrl) {
    this.modal.classList.add('is-open');
    document.body.style.overflow = 'hidden'; // Lock background scroll

    // Set video source
    if (this.videoPlayer) {
      if (videoUrl) {
        this.videoPlayer.src = videoUrl;
        this.videoPlayer.style.display = 'block';
        this.videoPlayer.load();
        this.videoPlayer.play().catch(err => {
          console.log('Video autoplay blocked or failed:', err);
        });
      } else {
        // Fallback lifestyle loop if video URL is missing
        this.videoPlayer.src = 'https://assets.mixkit.co/videos/preview/mixkit-girl-in-neon-sign-posing-for-camera-33827-large.mp4';
        this.videoPlayer.style.display = 'block';
        this.videoPlayer.load();
        this.videoPlayer.play().catch(err => {
          console.log('Video autoplay blocked or failed:', err);
        });
      }
    }

    // Reset quantities and states
    this.quantity = 1;
    this.detailsEl.style.display = 'none';
    this.loadingEl.style.display = 'flex';

    // Intercept mock product handle to return mocked data immediately
    if (handle === 'producto-de-ejemplo') {
      const mockProduct = {
        title: "Producto de Ejemplo",
        handle: "producto-de-ejemplo",
        featured_image: "https://cdn.shopify.com/placeholder-images/colors/product-1.png",
        price: 3800,
        compare_at_price: 4800,
        description: "Esta es la descripción de un producto de ejemplo para verificar el funcionamiento de la sección de Video Reels.",
        url: "#",
        options: [{ name: "Title", values: ["Default Title"] }],
        variants: [{
          id: 12345678,
          title: "Default Title",
          option1: "Default Title",
          price: 3800,
          compare_at_price: 4800,
          available: true,
          options: ["Default Title"]
        }]
      };
      setTimeout(() => {
        this.product = mockProduct;
        this.initializeDefaultOptions();
        this.renderProductDetails();
      }, 300);
      return;
    }

    // Fetch product details
    const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    fetch(`${rootUrl}products/${handle}.js`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch product data');
        return res.json();
      })
      .then(product => {
        this.product = product;
        this.initializeDefaultOptions();
        this.renderProductDetails();
      })
      .catch(err => {
        console.error(err);
        this.loadingEl.innerHTML = `<span style="color: #ef4444; font-weight: 600;">Error al cargar el producto.</span>`;
      });
  }

  closeModal() {
    this.modal.classList.remove('is-open');
    document.body.style.overflow = ''; // Unlock scroll

    // Stop and clear video player
    if (this.videoPlayer) {
      this.videoPlayer.pause();
      this.videoPlayer.src = '';
    }
  }

  initializeDefaultOptions() {
    this.selectedOptions = {};

    if (!this.product || !this.product.variants || this.product.variants.length === 0) {
      this.currentVariant = null;
      return;
    }

    // Default to first available variant
    let defaultVariant = this.product.variants.find(v => v.available);
    if (!defaultVariant) {
      defaultVariant = this.product.variants[0];
    }

    this.currentVariant = defaultVariant;

    if (defaultVariant && defaultVariant.options) {
      const options = this.product.options || [];
      options.forEach((opt, idx) => {
        const optName = typeof opt === 'string' ? opt : opt.name;
        this.selectedOptions[optName] = defaultVariant.options[idx];
      });
    }
  }

  formatMoney(cents) {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify.currency.active
      }).format(cents / 100);
    }
    const symbol = window.theme && window.theme.currency && window.theme.currency.symbol || '$';
    return symbol + (cents / 100).toFixed(2);
  }

  renderProductDetails() {
    const p = this.product;
    const v = this.currentVariant || p;

    // Featured Image of variant or product
    let mainImg = p.featured_image;
    if (this.currentVariant && this.currentVariant.featured_image) {
      mainImg = typeof this.currentVariant.featured_image === 'string'
        ? this.currentVariant.featured_image
        : (this.currentVariant.featured_image.src || p.featured_image);
    }

    // Calculate discount percent
    let discountPercent = 0;
    if (v && v.compare_at_price > v.price) {
      discountPercent = Math.round((v.compare_at_price - v.price) * 100 / v.compare_at_price);
    }

    let html = `
      <div class="video-quickview__thumb-row">
        <div class="video-quickview__thumb-info">
          ${discountPercent > 0 ? `<span class="video-quickview__badge">-${discountPercent}%</span>` : ''}
          <h4 class="video-quickview__title">${p.title}</h4>
        </div>
        ${mainImg ? `<img src="${mainImg}" alt="${p.title}" class="video-quickview__thumb">` : ''}
      </div>

      <div class="video-quickview__pricing">
        <span class="video-quickview__price">${this.formatMoney(v.price)}</span>
        ${v.compare_at_price > v.price ? `<span class="video-quickview__compare-price">${this.formatMoney(v.compare_at_price)}</span>` : ''}
      </div>

      <p class="video-quickview__desc">${p.description ? p.description.replace(/<[^>]*>/g, '').substring(0, 160) + '...' : ''}</p>
      
      <a href="${p.url}" class="video-quickview__view-link">
        Ver detalles 
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" width="12" height="12">
          <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
      </a>

      <div class="video-quickview__options">
    `;

    // Options mapping
    const options = p.options || [];
    options.forEach((option, idx) => {
      const optName = typeof option === 'string' ? option : option.name;
      const optValues = option.values || [];
      const optionNameLower = optName.toLowerCase();

      // Skip default option when there is no real variant info to show
      if (optionNameLower === 'title' && (optValues.length <= 1 || (optValues.length === 1 && optValues[0] === 'Default Title'))) {
        return;
      }

      const isColor = optionNameLower === 'color' || optionNameLower === 'colour' || optionNameLower === 'colors';
      const selectedValue = this.selectedOptions[optName] || '';

      html += `
        <div class="video-quickview__option-block" data-option-name="${optName}">
          <span class="video-quickview__option-label">${optName}: <span class="video-quickview__option-value-text">${selectedValue}</span></span>
          <div class="video-quickview__swatches">
      `;

      optValues.forEach(val => {
        const isSelected = selectedValue === val;
        const isSoldOut = !this.isOptionValueAvailable(optName, val);

        if (isColor) {
          const matchingVar = p.variants.find(item => item.options[idx] === val && item.featured_image);
          const swatchBgImage = matchingVar ? matchingVar.featured_image.src : '';

          html += `
            <button 
              type="button" 
              class="video-quickview__image-swatch ${isSelected ? 'is-selected' : ''} ${isSoldOut ? 'is-sold-out' : ''}" 
              data-value="${val.replace(/"/g, '&quot;')}"
              title="${val}"
              style="${swatchBgImage ? `background-image: url(${swatchBgImage});` : `background-color: ${val.toLowerCase()};`}"
            ></button>
          `;
        } else {
          html += `
            <button 
              type="button" 
              class="video-quickview__pill-swatch ${isSelected ? 'is-selected' : ''} ${isSoldOut ? 'is-sold-out' : ''}" 
              data-value="${val.replace(/"/g, '&quot;')}"
            >${val}</button>
          `;
        }
      });

      html += `
          </div>
        </div>
      `;
    });

    // Buy controls section
    html += `
      </div>
      
      <div class="video-quickview__buy-container">
        <span class="video-quickview__option-label">Quantity:</span>
        <div class="video-quickview__buy-row">
          <div class="video-quickview__qty-selector">
            <button type="button" class="video-quickview__qty-btn qty-minus">−</button>
            <input type="text" class="video-quickview__qty-input" value="${this.quantity}" readonly>
            <button type="button" class="video-quickview__qty-btn qty-plus">+</button>
          </div>

          <button 
            type="button" 
            class="video-quickview__add-to-cart" 
            ${v.available ? '' : 'disabled'}
            id="VideoQuickviewSubmit"
          >
            ${v.available ? 'Add to cart' : 'Out of stock'}
          </button>
        </div>
      </div>
    `;

    this.loadingEl.style.display = 'none';
    this.detailsEl.innerHTML = html;
    this.detailsEl.style.display = 'flex';

    this.setupModalControls();
  }

  isOptionValueAvailable(optionName, val) {
    if (!this.product.variants) return false;
    return this.product.variants.some(v => {
      if (!v.available) return false;
      const options = this.product.options || [];
      const idx = options.findIndex(opt => {
        const optName = typeof opt === 'string' ? opt : opt.name;
        return optName === optionName;
      });
      return v.options[idx] === val;
    });
  }

  setupModalControls() {
    // Swatch Clicks
    const swatches = this.detailsEl.querySelectorAll('.video-quickview__image-swatch, .video-quickview__pill-swatch');
    swatches.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const block = btn.closest('.video-quickview__option-block');
        const optionName = block.getAttribute('data-option-name');
        const val = btn.getAttribute('data-value');

        this.selectedOptions[optionName] = val;
        this.updateVariantMatch();
        this.renderProductDetails();
      });
    });

    // Quantity selectors
    const minus = this.detailsEl.querySelector('.qty-minus');
    const plus = this.detailsEl.querySelector('.qty-plus');
    const qtyInput = this.detailsEl.querySelector('.video-quickview__qty-input');

    if (minus && plus && qtyInput) {
      minus.addEventListener('click', () => {
        if (this.quantity > 1) {
          this.quantity--;
          qtyInput.value = this.quantity;
        }
      });

      plus.addEventListener('click', () => {
        this.quantity++;
        qtyInput.value = this.quantity;
      });
    }

    // Ajax add to cart
    const submit = this.detailsEl.querySelector('#VideoQuickviewSubmit');
    if (submit) {
      submit.addEventListener('click', (e) => {
        e.preventDefault();
        if (!this.currentVariant) return;

        submit.disabled = true;
        submit.textContent = 'Agregando...';

        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: new URLSearchParams({
            'id': this.currentVariant.id,
            'quantity': this.quantity
          }).toString()
        })
          .then(res => {
            if (!res.ok) throw new Error('Cart submission failed');
            return res.json();
          })
          .then(() => {
            submit.textContent = 'Added!';

            if (window.refreshCartDrawer) {
              window.refreshCartDrawer(true);
            }

            setTimeout(() => {
              this.closeModal();
            }, 600);
          })
          .catch(err => {
            console.error(err);
            submit.textContent = 'Error';
            submit.disabled = false;
          });
      });
    }
  }

  updateVariantMatch() {
    if (!this.product.variants) return;
    const options = this.product.options || [];
    const match = this.product.variants.find(v => {
      return options.every((opt, idx) => {
        const optName = typeof opt === 'string' ? opt : opt.name;
        return v.options[idx] === this.selectedOptions[optName];
      });
    });

    if (match) {
      this.currentVariant = match;
    }
  }
}

// Global initialization
function initAllVideoReels() {
  const sections = document.querySelectorAll('.video-reels-section-wrapper');
  sections.forEach(sec => {
    if (!sec.classList.contains('js-video-reels-initialized')) {
      new VideoReelsManager(sec);
      sec.classList.add('js-video-reels-initialized');
    }
  });
}

document.addEventListener('DOMContentLoaded', initAllVideoReels);
document.addEventListener('shopify:section:load', (e) => {
  const sectionWrapper = e.target.querySelector('.video-reels-section-wrapper');
  if (sectionWrapper) {
    new VideoReelsManager(sectionWrapper);
    sectionWrapper.classList.add('js-video-reels-initialized');
  }
});

// Run in case DOMContentLoaded fired before execution
if (document.readyState !== 'loading') {
  initAllVideoReels();
}
